import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import {
  VendorUser,
  getVendorToken,
  getStoredVendor,
  vendorLogin,
  vendorGetMe,
  vendorLogout,
} from "./vendorApi";

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR AUTH CONTEXT
   Manages vendor authentication state across the vendor portal
   ═══════════════════════════════════════════════════════════════════════════ */

interface VendorAuthState {
  vendor: VendorUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const VendorAuthContext = createContext<VendorAuthState | undefined>(undefined);

export const useVendorAuth = (): VendorAuthState => {
  const ctx = useContext(VendorAuthContext);
  if (!ctx) throw new Error("useVendorAuth must be used within VendorAuthProvider");
  return ctx;
};

export const VendorAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [vendor, setVendor] = useState<VendorUser | null>(getStoredVendor);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!vendor;

  /* On mount, verify token is still valid */
  useEffect(() => {
    const token = getVendorToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    vendorGetMe()
      .then((v) => setVendor(v))
      .catch(() => {
        setVendor(null);
        localStorage.removeItem("vendor_token");
        localStorage.removeItem("vendor_user");
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const { vendor: v } = await vendorLogin(email, password);
      setVendor(v);
    } catch (err: any) {
      const msg = err?.message || "Login failed. Please check your credentials.";
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await vendorLogout();
    setVendor(null);
  }, []);

  return (
    <VendorAuthContext.Provider value={{ vendor, isAuthenticated, isLoading, error, login, logout }}>
      {children}
    </VendorAuthContext.Provider>
  );
};
